import type {
  Compliance,
  ComplianceParserOptions,
  IPlayerConfigResponse,
  ParserOptions,
  PlayerConfigType,
} from './types';

export const playerConfigParser = (
  response: IPlayerConfigResponse,
  options: ParserOptions
): { parsedConfig: PlayerConfigType; compliance: Compliance } => {
  return {
    parsedConfig: {
      countryCode: response.playerIPCountry,
      currencyMultiplier: response.currencyMultiplier,
      isBanned: response.params.isBanned ?? false,
      jwtToken: response.jwtToken ?? '',
      jurisdiction: response.jurisdiction,
      netWinBalanceMap: response.netWinBalanceMap,
      screenName: response.screenName,
      showOtherMessages: response.params.showothermessages ?? true,
      userId: response.userId,
      userType: response.userType,
      selectedChipIndex: parseGameIdString(response.params.selected_chip, undefined, {
        gameId: options.operatorGameId,
        valueTransformer: parseInt,
      }),
      selectedChipParamsRaw: response.params.selected_chip
        ? { selected_chip: response.params.selected_chip }
        : undefined,
      freeChipsParamsRaw: response.params.selected_chip_fb
        ? { selected_chip_fb: response.params.selected_chip_fb }
        : undefined,
      videoStream: parseGameIdString(response.params.video_stream, true, {
        gameId: options.operatorGameId,
        // Legacy game versions enable video stream for value `false` and disable for `true`.
        // Keeping inverse logic here to avoid breaking player config on the server.
        valueTransformer: (value: string) => value === 'false',
      }),
      videoStreamRaw: response.params.video_stream ? { video_stream: response.params.video_stream } : undefined,

      operatorId: response.operatorId,
      videoVolume: parseParamNumber(response.params.video_sounds, 1, (value: number) => value / 100),
      gameEffectsVolume: parseParamNumber(response.params.game_effects, 1, (value: number) => value / 100),
      backgroundMusicVolume: parseParamNumber(response.params.bg_music, 1, (value: number) => value / 100),
      voiceOverVolume: parseParamNumber(response.params.vo_music, 1, (value: number) => value / 100),
      lobbyFilters: parseLobbyFilters(response.params.lobby_filters),
      isSecondBetboardEnabled: response.params.issecondbetboardenabled ? response.params.issecondbetboardenabled : true,
      hfAnimationEnabled: response.params.hf_animation_enabled ? response.params.hf_animation_enabled : true,
      lobbyFavourites: parseLobbyFavourites(response.params.lobbyfavourites),
      bcBonusBetShow: response.params.bcbonusbetshow ?? true,
      bcPairBets: response.params.bcpairbets ?? true,
      bcLightTheme: response.params.bclighttheme ?? true,
      bcHasNoCommission: response.params.isnocommission ?? true,
      decisionstrategystatus: response.params.decisionstrategystatus,
      autostand: response.params.autostand,
      autohit: response.params.autohit,
      autoinsurance: response.params.autoinsurance,
      splitMinRange: response.params.showdec_split_min,
      splitMaxRange: response.params.showdec_split_max,
      doubleMinRange: response.params.showdec_double_min,
      doubleMaxRange: response.params.showdec_double_max,
      betBehindSplit: response.params.betbehind_split,
      betBehindDouble: response.params.betbehind_doubledown,
      betBehindInsurance: response.params.betbehind_insurance,
      bcShowBettingStats: response.params.showbettingstats ?? true,
      autoplayOnboarded: response.params.autoplay_onboarded ?? false,
      hfHideMyBets: response.params.hfhidemybets ?? false,

      compliance: parseCompliance({
        jurisdiction: response.jurisdiction,
        dedicatedLanguage: options.dedicatedLanguage,
        isCrashGame: options.isCrashGame,
        currentLanguage: options.currentLanguage,
      }),
    },
    compliance: parseCompliance({
      jurisdiction: response.jurisdiction,
      dedicatedLanguage: options.dedicatedLanguage,
      isCrashGame: options.isCrashGame,
      currentLanguage: options.currentLanguage,
    }),
  };
};

export const getComplianceByJurisdiction = ({
  jurisdiction,
  dedicatedLanguage,
  isCrashGame,
  currentLanguage,
}: ComplianceParserOptions): Compliance => {
  switch (jurisdiction) {
    case 'CH':
      return {
        maxPlayerLimitInfo: true,
      };
    case 'CO':
      return {
        creditHelpInfo: true,
      };
    case 'IT':
      return {
        waitResultBeforeLeave: true,
        sessionBalance: !isCrashGame,
        showSessionId: true,
        isRestrictedToLeaveWithActiveBet: true,
      };
    case 'ON':
      return {
        netPosition: true,
        showSessionTimer: true,
      };
    case 'UK':
      return {
        displayBonusBalance: true,
        netPosition: true,
        showSessionTimer: true,
      };
    case 'BR':
      return {
        disableStudioSound: dedicatedLanguage !== 'pt',
        disableVoiceOverSound: currentLanguage !== 'pt',
      };
    default:
      return {
        waitResultBeforeLeave: false,
        showSessionTimer: false,
        showSessionId: false,
        displayBonusBalance: false,
        sessionBalance: false,
        creditHelpInfo: false,
        maxPlayerLimitInfo: false,
        netPosition: false,
        hideCurrency: false,
        isRestrictedToLeaveWithActiveBet: false,
        disableStudioSound: false,
        disableVoiceOverSound: false,
      };
  }
};

export const parseCompliance = (options: ComplianceParserOptions): Compliance => {
  // TODO: make server send compliance as flags and replace this function
  return getComplianceByJurisdiction(options);
};

const gameRegEx = /^[^,=]+$/;
export const parseParam = <T>(
  playerConfigParams: Array<Record<string, string>> | null | undefined,
  substring: string,
  defaultValue: T,
  gameId?: string,
  valueTransformer?: (value: string) => T
): T => {
  const paramValue = playerConfigParams?.find((item) => substring in item)?.[substring];

  if (!paramValue) {
    return defaultValue;
  }

  if (!gameId) {
    /**
     * In the case of a single value string with no gameId, apply valueTransformer if provided.
     * Otherwise, return the singleValue as T.
     */
    const singleValue = gameRegEx.test(paramValue) ? paramValue : (defaultValue as string);
    return valueTransformer?.(singleValue) ?? (singleValue as T);
  }

  /**
   * Most param values are usually stored per game in a list, e.g. "1000=true,1100=true,1101=false".
   * Use RegExp to find value corresponding to provided gameId inside the list.
   */
  return parseGameIdString(paramValue, defaultValue, { gameId, valueTransformer });
};

export const parseParamNumber = <ValueType>(
  value: number | undefined,
  defaultValue: ValueType,
  valueTransformer?: (value: number) => ValueType
): ValueType => {
  if (value === undefined) return defaultValue;

  return valueTransformer?.(value) ?? (value as ValueType);
};

export const parseGameIdString = <ValueType>(
  string: string,
  defaultValue: ValueType,
  options?: {
    gameId?: string;
    valueTransformer?: (value: string) => ValueType;
  }
): ValueType => {
  const regex = new RegExp(`\\b${options?.gameId}=(\\w+)`);
  const matchGameId = regex.exec(string)?.[1];

  if (!matchGameId) {
    return defaultValue;
  }
  return options?.valueTransformer?.(matchGameId) ?? (matchGameId as ValueType);
};

const ampRegEx = /amp;/g;
const quotRegEx = /&quot;/g;
export const parseStringForJson = (value: string): string => {
  return value.replace(ampRegEx, '').replace(quotRegEx, '"');
};

/**
 * Parses lobby filters from player config response.
 * @param lobbyFilters - stringified JSON from player config response
 * @returns parsed lobby filters
 */
export const parseLobbyFilters = (lobbyFilters?: string): Record<string, Record<string, string[]>> => {
  if (!lobbyFilters) return {};
  const convertedToStringForJson = parseStringForJson(lobbyFilters);
  try {
    return JSON.parse(convertedToStringForJson);
  } catch {
    /**
     * This error is intentionally swallowed because while playerConfig is essential, these lobby filters are not and have a default fallback.
     * If an ParsingError would be thrown here, it would stop lobby/game from working which in this case is not desired.
     */
    console.error('Error parsing lobby filters', convertedToStringForJson);
    return {};
  }
};

export const parseParamLobbyFilters = (
  playerConfigParams: Array<Record<string, string>> | undefined,
  substring: string
): Record<string, Record<string, string[]>> => {
  const paramValue = playerConfigParams?.find((item) => substring in item)?.[substring];

  if (!paramValue) {
    return {};
  }

  return parseLobbyFilters(paramValue);
};

export const getValueByKey = (playerConfigParams: Record<string, string>[], key: string): string => {
  const dataItem = playerConfigParams.find((item) => key in item);
  return dataItem?.[key] || '';
};

export const parseNetWinBalanceString = (nwbString: string): Record<string, number> => {
  const netWinBalanceObj: Record<string, number> = {};
  return nwbString.split(',').reduce((acc, cur) => {
    const [tableId, nwbValue] = cur.split('=');
    if (tableId) acc[tableId] = Number(nwbValue);
    return acc;
  }, netWinBalanceObj);
};

export const parseLobbyFavourites = (lobbyFavourites?: string | number): string[] => {
  switch (typeof lobbyFavourites) {
    case 'string':
      if (lobbyFavourites === '') return [];
      return lobbyFavourites.split(',');
    case 'number':
      return [lobbyFavourites.toString()];
    default:
      return [];
  }
};
